import React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';

import { usePromoClicked } from '../helpers/analyticsPromoUtils';
import Modules from '../shared_modules/Modules';

import {
  AlertContainer,
  CloseButton,
  Container,
  Heading2,
  Heading3,
  Link,
  Modal,
  ModalBody,
} from './StyledElements';

export function AnalyticsPromoModalLink({ children }: { children: React.ReactNode }) {
  const { hidePromo, setPromoClicked } = usePromoClicked();

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [hasChatBeenClicked, setHasChatBeenClicked] = React.useState(false);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const isV2 = Modules.hasModule('analytics.promo.v2');

    if (isV2) {
      setIsModalOpen(true);
    } else {
      window.Intercom?.('trackEvent', 'interested-in-analytics');
      setPromoClicked();
      hidePromo(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);

    if (hasChatBeenClicked) {
      setPromoClicked();
    }
  };

  const handleChatClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.Intercom?.('trackEvent', 'interested-in-analytics');
    setHasChatBeenClicked(true);
  };

  return (
    <>
      <Link href="#" onClick={handleLinkClick}>
        {children}
      </Link>
      <Modal isOpen={isModalOpen} onRequestClose={closeModal}>
        <ModalBody>
          <Container>
            {hasChatBeenClicked && (
              <AlertContainer>
                <Alert
                  title="An account manager will reach out to you by email soon."
                  type="success"
                  noCloseBtn
                />
              </AlertContainer>
            )}
            <Flex style={{ paddingRight: '7px' }} alignItems="center">
              <Box>
                <Heading2>Stop Guessing, Start Growing:</Heading2>
                <Text pb="12px" pt="12px">
                  Lightspeed Analytics Dynamic Reorder Reports leverage your real sales data to
                  deliver precise order recommendations, ensuring you have the right products, in
                  the right quantity, exactly when your customers need them.
                </Text>
                <Button
                  variant="secondary"
                  fill
                  id="analytics-promo-chat-button-top"
                  onClick={handleChatClick}
                  disabled={hasChatBeenClicked}
                >
                  Chat with an expert
                </Button>
              </Box>
              <img
                src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/analytics/promo/analytics_promo_chart.png"
                alt="Analytics Promotional Chart"
                width="484"
                height="339"
              />
            </Flex>
            <Box backgroundColor="white">
              <Heading3 pt="32px" pb="64px">
                The Dynamic Reorder report gives you intelligent, real-time inventory control—so you
                always stock what sells and avoid what doesn’t.
              </Heading3>
              <Flex backgroundColor="white" pb="64px">
                <img
                  src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/analytics/promo/analytics_promo_pos.png"
                  alt="Analytics Promotional Point of Sale"
                  width="468"
                  height="327"
                />
                <Flex flexDirection="column" justifyContent="space-between" ml="32px">
                  <Box>
                    <Heading3>Smarter, proactive inventory management</Heading3>
                    <Text mt="6px">
                      The report flags items at risk of running out and recommends exactly what to
                      reorder. It also surfaces products that are dragging down performance, helping
                      you avoid unnecessary overstock.
                    </Text>
                  </Box>
                  <Box>
                    <Heading3>Clear, data-backed recommendations</Heading3>
                    <Text mt="6px">
                      Every suggestion is driven by your own sales and inventory trends. The report
                      groups results by shop and provides precise, trend-based guidance in the
                      Quantity to Order column, so you know exactly what action to take.
                    </Text>
                  </Box>
                </Flex>
              </Flex>
              <Box>
                <Heading3>Fast filtering to focus on what matters</Heading3>
                <Text mt="6px" pb="32px">
                  Start with a full, multi-store view, then instantly filter by store, category,
                  brand, or any other dimension to drill into the areas that need attention.
                </Text>
                <Heading3>Easy sharing and automated delivery</Heading3>
                <Text mt="6px" pb="32px">
                  Save the report for quick access, share it across your company (anyone with a
                  Lightspeed Analytics account), and schedule automatic email delivery daily,
                  weekly, or monthly to keep teams aligned and proactive.
                </Text>
              </Box>
            </Box>
            <Box>
              <Heading3 pt="32px">Learn more about Lightspeed Analytics</Heading3>
              <Text mt="6px">
                Lightspeed Analytics gives you a clear, real-time view of your business. From sales
                and inventory performance to customer trends and staff productivity. Go beyond basic
                reports with insights that help you spot opportunities faster, plan with confidence,
                and make smarter decisions that drive growth.
              </Text>
              <Heading3 pt="32px" pb="32px">
                For a in-depth look at Lightspeed Analytics Dynamic Reorder reporting have a look
                here:
              </Heading3>
            </Box>
            <Flex alignItems="center" justifyContent="center" pb="32px">
              <iframe
                id="analytics-promo-video"
                title="vimeo-player"
                src="https://player.vimeo.com/video/1103740051?h=e69fbfd7dc"
                width="640"
                height="360"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
                style={{ border: 'none' }}
              />
            </Flex>
            <Flex
              backgroundColor="#000"
              flexDirection="column"
              height="237px"
              justifyContent="center"
            >
              <Heading3 textAlign="center" color="#FFF" pb="32px">
                Smarter and faster reordering to maximize profits.
              </Heading3>
              <Flex justifyContent="center" alignItems="center">
                <Button
                  variant="secondary"
                  fill
                  id="analytics-promo-chat-button-bottom"
                  onClick={handleChatClick}
                  disabled={hasChatBeenClicked}
                >
                  Chat with an expert
                </Button>
              </Flex>
            </Flex>
            <CloseButton onClick={closeModal} aria-label="Close modal" className="notranslate">
              ×
            </CloseButton>
          </Container>
        </ModalBody>
      </Modal>
    </>
  );
}
